/**
 * Line/word masked reveals: clip vertically like overflow:hidden; widen the clip box horizontally
 * so glyph overshoot is not cut off (Pretext/canvas vs real paint). Left/right: generous px bleed.
 */
var LINE_TEXT_MASK_CLIP_DURING_REVEAL = 'inset(0 -200px 0 -200px)'

function usesAppleWebKit() {
	if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
		return false
	}
	return /AppleWebKit/i.test(window.navigator.userAgent || '') && /Apple/i.test(window.navigator.vendor || '')
}

/**
 * Browsers do not reliably keep an ancestor's `background-clip:text` paint on the generated
 * line/word/character descendants. Text animations transform those descendants, so the gradient
 * can remain on the block while its text becomes transparent. Give every generated text unit the
 * matching slice of the block's gradient.
 */
function prepareGradientTextAnimation(target) {
	if (!target || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
		return
	}

	var blocks = target.querySelectorAll('p, h1, h2, h3, h4, h5, h6')
	blocks.forEach(function (block) {
		var blockStyle = window.getComputedStyle(block)
		var backgroundClip = blockStyle.webkitBackgroundClip || blockStyle.backgroundClip
		if (backgroundClip !== 'text' || !blockStyle.backgroundImage || blockStyle.backgroundImage === 'none') {
			return
		}

		var blockRect = block.getBoundingClientRect()
		if (blockRect.width <= 0 || blockRect.height <= 0) {
			return
		}

		var characters = block.querySelectorAll('.lay-text-line .char')
		var words = block.querySelectorAll('.lay-text-line .word')
		var units = characters.length > 0 ? characters : words.length > 0 ? words : block.querySelectorAll('.lay-text-line')
		units.forEach(function (unit) {
			var previousTransform = unit.style.transform
			var previousWebkitTransform = unit.style.webkitTransform
			if (previousTransform || previousWebkitTransform) {
				unit.style.transform = 'none'
				unit.style.webkitTransform = 'none'
			}
			var unitRect = unit.getBoundingClientRect()
			if (previousTransform || previousWebkitTransform) {
				unit.style.transform = previousTransform
				unit.style.webkitTransform = previousWebkitTransform
			}

			unit.style.backgroundImage = blockStyle.backgroundImage
			unit.style.backgroundSize = blockRect.width + 'px ' + blockRect.height + 'px'
			unit.style.backgroundPosition =
				(blockRect.left - unitRect.left) + 'px ' + (blockRect.top - unitRect.top) + 'px'
			unit.style.backgroundRepeat = 'no-repeat'
			unit.style.backgroundClip = 'text'
			unit.style.webkitBackgroundClip = 'text'
			unit.style.color = 'transparent'
			unit.style.webkitTextFillColor = 'transparent'

			// `background-clip:text` only paints inside the unit's own box. A glyph
			// descender can extend below that box even though the surrounding line
			// mask has padding. Mirror the enabled mask padding onto the generated
			// unit and cancel it with a negative margin so the gradient reaches the
			// descender without changing the line spacing.
			var mask = unit.closest('.lay-text-line-mask')
			var paddingBottom = mask ? parseFloat(window.getComputedStyle(mask).paddingBottom) || 0 : 0
			if (paddingBottom > 0) {
				unit.style.paddingBottom = paddingBottom + 'px'
				unit.style.marginBottom = -paddingBottom + 'px'
			}
		})
	})
}

/**
 * @param {NodeListOf<Element>|Element[]} lineMasks
 * @param {boolean} duringReveal
 */
function setLineTextMaskRevealClip(lineMasks, duringReveal) {
	if (!lineMasks || !lineMasks.forEach) {
		return
	}
	var clip = duringReveal ? LINE_TEXT_MASK_CLIP_DURING_REVEAL : 'none'
	lineMasks.forEach(function (el) {
		if (!el || !el.style) {
			return
		}
		var elementClip = clip
		// Safari clips transformed descendants against the mask's original
		// border box and can ignore the extra padding when resolving clip-path.
		// Extend the clip itself by that padding so descenders stay visible.
		if (duringReveal && usesAppleWebKit() && typeof window.getComputedStyle === 'function') {
			var paddingBottom = parseFloat(window.getComputedStyle(el).paddingBottom) || 0
			if (paddingBottom > 0) {
				elementClip = 'inset(0 -200px -' + Math.ceil(paddingBottom) + 'px -200px)'
			}
		}
		el.style.overflow = 'visible'
		el.style.setProperty('clip-path', elementClip)
		el.style.setProperty('-webkit-clip-path', elementClip)
	})
}

var MASKED_TEXT_REVEAL_MIN_BLEED_PX = 14

function getMaskedTextRevealBleedPx(el) {
	const cs = typeof window !== 'undefined' ? window.getComputedStyle(el) : null
	const fontSize = cs ? parseFloat(cs.fontSize) : 0
	return Math.ceil(Math.max(MASKED_TEXT_REVEAL_MIN_BLEED_PX, (fontSize || 0) * 0.18))
}

function getMaskedTextRevealStartY(el) {
	if (!el || typeof el.getBoundingClientRect !== 'function') {
		return 0
	}

	const mask = typeof el.closest === 'function' ? el.closest('.lay-text-line-mask') : null
	const previousTransform = el.style.transform
	const previousWebkitTransform = el.style.webkitTransform
	if (previousTransform || previousWebkitTransform) {
		el.style.transform = 'none'
		el.style.webkitTransform = 'none'
	}
	const elRect = el.getBoundingClientRect()
	const maskRect = mask && typeof mask.getBoundingClientRect === 'function' ? mask.getBoundingClientRect() : null
	if (previousTransform || previousWebkitTransform) {
		el.style.transform = previousTransform
		el.style.webkitTransform = previousWebkitTransform
	}

	const distanceFromTargetTopToMaskBottom = maskRect ? maskRect.bottom - elRect.top : 0
	const height = Math.max(
		elRect.height || 0,
		maskRect ? maskRect.height || 0 : 0,
		distanceFromTargetTopToMaskBottom || 0
	)

	const bleed = getMaskedTextRevealBleedPx(el)
	if (height > 0) {
		const startY = Math.ceil(height) + bleed
		if (typeof window !== 'undefined' && window.layTextAnimationDebug === true) {
			console.log('[lay-text-animation] masked reveal start', JSON.stringify({
				startY,
				bleed,
				targetTag: el.tagName,
				targetClass: el.className,
				targetText: (el.textContent || '').trim().slice(0, 80),
				targetHeight: Math.round((elRect.height || 0) * 100) / 100,
				maskHeight: maskRect ? Math.round((maskRect.height || 0) * 100) / 100 : null,
				targetTopToMaskBottom: Math.round((distanceFromTargetTopToMaskBottom || 0) * 100) / 100,
				hadTransformBeforeMeasure: !!(previousTransform || previousWebkitTransform),
			}))
		}
		return startY
	}

	const cs = typeof window !== 'undefined' ? window.getComputedStyle(el) : null
	const lineHeight = cs ? parseFloat(cs.lineHeight) : 0
	const fontSize = cs ? parseFloat(cs.fontSize) : 0
	return Math.ceil(lineHeight || fontSize || 0) + bleed
}

var getEasedStaggerDelayForTransitionOut = function (index, length, target, durationOverride) {
	// Get the data-duration value or use default 1000
	const duration = typeof durationOverride === 'number' ? durationOverride : parseInt(target.getAttribute('data-duration')) || 500
	var toAdd = duration / length
	var normalDelay = index * toAdd
	var maxDelay = length * toAdd
	var mappedVal = map(normalDelay, 0, maxDelay, 0, 1)
	var easedVal = easeOutQuart(mappedVal)
	var val = normalDelay * easedVal
	return val
}

var getEasedStaggerDelayForTransitionOutAlt = function (index, length, target, durationOverride) {
	// Inter-segment stagger calculation: ms between successive
	// word/char/line starts. Priority order:
	//   1. `data-stagger` (explicit user-facing knob in the Text
	//      Animation sidebar) — represents "ms between sub-items",
	//      what the user actually means when they tune the cascade.
	//   2. `durationOverride` arg — passed by callers that want to
	//      compute against a custom total (currently unused for the
	//      legacy text-anim path; reserved for future external resolves).
	//   3. `data-duration` legacy fallback — old data where the user's
	//      duration field was repurposed as the inter-item gap. Kept
	//      so pages built before the explicit Stagger field still
	//      animate.
	var staggerAttr = parseInt(target.getAttribute('data-stagger'))
	var baseDelay
	if (isFinite(staggerAttr) && staggerAttr > 0) {
		baseDelay = staggerAttr
	} else if (typeof durationOverride === 'number') {
		baseDelay = durationOverride
	} else {
		baseDelay = parseInt(target.getAttribute('data-duration')) || 40
	}
	return getTextAnimationStaggerDelay(index, length, baseDelay, getTextAnimationStaggerEasingForTarget(target))
}

// https://github.com/AndrewRayCode/easing-utils/blob/master/src/easing.js
// pass in a value between 0 and 1, and get the eased value back

// Decelerating to zero velocity
function easeOutQuart(t) {
	const t1 = t - 1
	return 1 - t1 * t1 * t1 * t1
}

var textAnimationStaggerEasingFns = {
	linear: function (t) { return t },
	easeInQuad: function (t) { return t * t },
	easeOutQuad: function (t) { return t * (2 - t) },
	easeInOutQuad: function (t) { return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t },
	easeInCubic: function (t) { return t * t * t },
	easeOutCubic: function (t) { t = t - 1; return t * t * t + 1 },
	easeInOutCubic: function (t) { return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1 },
	easeInQuart: function (t) { return t * t * t * t },
	easeOutQuart: easeOutQuart,
	easeInOutQuart: function (t) { return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t },
	easeOutQuint: function (t) { t = t - 1; return 1 + t * t * t * t * t },
	easeInSine: function (t) { return 1 - Math.cos((t * Math.PI) / 2) },
	easeOutSine: function (t) { return Math.sin((t * Math.PI) / 2) },
	easeInOutSine: function (t) { return -(Math.cos(Math.PI * t) - 1) / 2 },
	easeInExpo: function (t) { return t === 0 ? 0 : Math.pow(2, 10 * (t - 1)) },
	easeOutExpo: function (t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t) },
	easeInOutExpo: function (t) {
		if (t === 0) return 0
		if (t === 1) return 1
		return t < 0.5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2
	},
	easeOutCirc: function (t) { t = t - 1; return Math.sqrt(1 - t * t) },
	easeInOutCirc: function (t) {
		return t < 0.5
			? (1 - Math.sqrt(1 - Math.pow(2 * t, 2))) / 2
			: (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2
	},
	easeOutBack: function (t) {
		var c1 = 1.70158
		var c3 = c1 + 1
		return Math.max(0, Math.min(1, 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)))
	},
	easeInOutBack: function (t) {
		var c1 = 1.70158
		var c2 = c1 * 1.525
		var raw = t < 0.5
			? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
			: (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2
		return Math.max(0, Math.min(1, raw))
	},
}

function sampleTextAnimationStaggerEasing(name, t) {
	var fn = textAnimationStaggerEasingFns[name] || textAnimationStaggerEasingFns.easeOutQuart
	return fn(Math.max(0, Math.min(1, t)))
}

function getTextAnimationStaggerEasingForTarget(target) {
	if (!target || typeof target.getAttribute !== 'function') {
		return 'easeOutQuart'
	}
	var v = target.getAttribute('data-stagger-easing')
	if (v && TEXT_ANIMATION_EASING_ALLOWLIST.indexOf(v) !== -1) {
		return v
	}
	return 'easeOutQuart'
}

function getTextAnimationStaggerDelay(index, length, baseDelay, easingName) {
	var normalDelay = index * baseDelay
	if (easingName === 'linear' || length <= 0 || baseDelay <= 0) {
		return normalDelay
	}
	var maxDelay = length * baseDelay
	var mappedVal = maxDelay > 0 ? map(normalDelay, 0, maxDelay, 0, 1) : 0
	var easedVal = sampleTextAnimationStaggerEasing(easingName, mappedVal)
	return normalDelay * easedVal
}

function map(value, in_min, in_max, out_min, out_max) {
	return ((value - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min
}

/** Must match gridder TextAnimationSection options + PHP whitelist in text.php */
var TEXT_ANIMATION_EASING_ALLOWLIST = [
	'linear',
	'easeInQuad',
	'easeInCubic',
	'easeInQuart',
	'easeInSine',
	'easeInExpo',
	'easeOutQuad',
	'easeOutCubic',
	'easeOutQuart',
	'easeOutQuint',
	'easeOutSine',
	'easeOutExpo',
	'easeOutCirc',
	'easeOutBack',
	'easeInOutQuad',
	'easeInOutCubic',
	'easeInOutQuart',
	'easeInOutSine',
	'easeInOutExpo',
	'easeInOutCirc',
	'easeInOutBack',
]

function getTextAnimationEasingForTarget(target) {
	if (!target || typeof target.getAttribute !== 'function') {
		return 'easeOutCubic'
	}
	var v = target.getAttribute('data-animation-easing')
	if (v && TEXT_ANIMATION_EASING_ALLOWLIST.indexOf(v) !== -1) {
		return v
	}
	return 'easeOutCubic'
}

export {
	getEasedStaggerDelayForTransitionOut,
	getEasedStaggerDelayForTransitionOutAlt,
	map,
	easeOutQuart,
	getTextAnimationEasingForTarget,
	getTextAnimationStaggerDelay,
	getTextAnimationStaggerEasingForTarget,
	TEXT_ANIMATION_EASING_ALLOWLIST,
	setLineTextMaskRevealClip,
	getMaskedTextRevealStartY,
	prepareGradientTextAnimation,
}
